<script setup lang="ts">
import { ref } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import Chip from '@uiKit/elements/Chip.vue'
import Button from '@uiKit/elements/Button.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import { documentStatusMetaOf, documentTypeLabelOf } from '../../constants/propertyLifecycle'
import { downloadDocument, viewDocument } from '../../utils/propertyDocumentUtils'
import type { PropertyDocument } from '../../types/propertyTypes'

// The property's current documents (superseded versions are filtered server-side),
// each openable in a new tab or saved to disk.
//
// Saving is asynchronous — the file is fetched and handed over as a blob, because
// a cross-origin `download` attribute is ignored by the browser — so the row
// tracks which document is in flight and shows it on that button alone.
withDefaults(
  defineProps<{
    documents: PropertyDocument[]
    /** Review status chip per row; off for documents that carry none (settlement). */
    showStatus?: boolean
    emptyMessage?: string
  }>(),
  { showStatus: true, emptyMessage: 'No documents on file for this property yet.' },
)

const savingDocumentId = ref<string | null>(null)

const KB = 1024

// The Ejari carried over from the sell request has no recorded size.
function metaOf(document: PropertyDocument): string {
  if (document.fileSizeBytes <= 0) return document.fileName
  return `${document.fileName} · ${fileSizeLabel(document.fileSizeBytes)}`
}

function fileSizeLabel(bytes: number): string {
  if (bytes < KB) return `${bytes} B`
  const kilobytes = bytes / KB
  if (kilobytes < KB) return `${Math.round(kilobytes)} KB`
  return `${(kilobytes / KB).toFixed(1)} MB`
}

async function handleDownload(document: PropertyDocument) {
  savingDocumentId.value = document.id
  try {
    await downloadDocument(document.fileUrl, document.fileName)
  } finally {
    savingDocumentId.value = null
  }
}
</script>

<template>
  <Box direction="col" gap="3">
    <Text v-if="!documents.length" type="caption" color="muted">{{ emptyMessage }}</Text>

    <Box
      v-for="document in documents"
      :key="document.id"
      flex
      align="center"
      gap="3"
      class="doc-row"
    >
      <PiIcon name="file" class="doc-row__icon" />
      <Box direction="col" gap="1" grow class="doc-row__body">
        <Text type="body" weight="medium" truncate>
          {{ documentTypeLabelOf(document.documentType) }}
        </Text>
        <Text type="caption-sm" color="muted" truncate>
          {{ metaOf(document) }}
        </Text>
      </Box>

      <Chip
        v-if="showStatus"
        :label="documentStatusMetaOf(document.documentStatus).label"
        :color="documentStatusMetaOf(document.documentStatus).color"
        variant="outline"
        size="small"
      />

      <Text v-if="!document.fileUrl" type="caption" color="muted" class="doc-row__pending">
        Not available yet
      </Text>
      <Box v-else flex align="center" gap="1" class="doc-row__actions">
        <Button
          icon="eye"
          variant="text"
          class="doc-row__action"
          :aria-label="`View ${documentTypeLabelOf(document.documentType)}`"
          @click="viewDocument(document.fileUrl)"
        />
        <Button
          icon="download"
          variant="text"
          class="doc-row__action"
          :loading="savingDocumentId === document.id"
          :aria-label="`Download ${documentTypeLabelOf(document.documentType)}`"
          @click="handleDownload(document)"
        />
      </Box>
    </Box>
  </Box>
</template>

<style scoped>
.doc-row {
  padding: 0.875rem 1rem;
  border: 1px solid var(--p-content-border-color);
  border-radius: var(--p-border-radius-md);
  background: var(--p-surface-0);
}

.doc-row__icon {
  font-size: 1rem;
  color: var(--p-text-muted-color);
  flex-shrink: 0;
}

.doc-row__body {
  min-width: 0;
}

.doc-row__actions,
.doc-row__pending {
  flex-shrink: 0;
}

/* Icon-only, so the hit area comes from here rather than from a label. */
.doc-row__action {
  min-width: 2.5rem;
  min-height: 2.5rem;
}
</style>
